import { useTranslation } from 'react-i18next';
import { Shield, Bot, Mic, BarChart3, Clock, UserCheck, FileDown, Trash2, Cookie } from 'lucide-react';

function Section({ icon: Icon, titleKey, children }: { icon: React.ElementType; titleKey: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">{t(titleKey)}</h2>
      </div>
      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

export default function DataPrivacyPage() {
  const { t } = useTranslation();

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-heading text-foreground">{t('dataPrivacy.pageTitle')}</h1>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{t('dataPrivacy.pageSubtitle')}</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* What data we collect */}
        <Section icon={BarChart3} titleKey="dataPrivacy.whatWeCollect.title">
          <p>{t('dataPrivacy.whatWeCollect.intro')}</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('dataPrivacy.whatWeCollect.account')}</li>
            <li>{t('dataPrivacy.whatWeCollect.documents')}</li>
            <li>{t('dataPrivacy.whatWeCollect.transcripts')}</li>
            <li>{t('dataPrivacy.whatWeCollect.kpis')}</li>
            <li>{t('dataPrivacy.whatWeCollect.scores')}</li>
            <li>{t('dataPrivacy.whatWeCollect.strengths')}</li>
            <li>{t('dataPrivacy.whatWeCollect.duration')}</li>
          </ul>
        </Section>

        {/* AI & third-party services */}
        <Section icon={Bot} titleKey="dataPrivacy.aiServices.title">
          <p>{t('dataPrivacy.aiServices.intro')}</p>
          <ul className="ml-4 list-disc space-y-1">
            <li><strong>{t('dataPrivacy.aiServices.voiceLabel')}</strong> — {t('dataPrivacy.aiServices.elevenlabs')}</li>
            <li><strong>{t('dataPrivacy.aiServices.aiLabel')}</strong> — {t('dataPrivacy.aiServices.claude')}</li>
          </ul>
          <p className="mt-2">{t('dataPrivacy.aiServices.international')}</p>
          <p className="mt-1">{t('dataPrivacy.aiServices.providersOnRequest')}</p>
        </Section>

        {/* Voice processing */}
        <Section icon={Mic} titleKey="dataPrivacy.voice.title">
          <p>{t('dataPrivacy.voice.description')}</p>
          <p>{t('dataPrivacy.voice.noStorage')}</p>
        </Section>

        {/* Who sees your data */}
        <Section icon={UserCheck} titleKey="dataPrivacy.visibility.title">
          <p>{t('dataPrivacy.visibility.description')}</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('dataPrivacy.visibility.you')}</li>
            <li>{t('dataPrivacy.visibility.manager')}</li>
            <li>{t('dataPrivacy.visibility.teamRanking')}</li>
          </ul>
        </Section>

        {/* Data retention */}
        <Section icon={Clock} titleKey="dataPrivacy.retention.title">
          <p>{t('dataPrivacy.retention.description')}</p>
        </Section>

        {/* Your rights */}
        <Section icon={FileDown} titleKey="dataPrivacy.rights.title">
          <p>{t('dataPrivacy.rights.intro')}</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('dataPrivacy.rights.access')}</li>
            <li>{t('dataPrivacy.rights.rectification')}</li>
            <li>{t('dataPrivacy.rights.deletion')}</li>
            <li>{t('dataPrivacy.rights.portability')}</li>
            <li>{t('dataPrivacy.rights.objection')}</li>
          </ul>
          <p className="mt-2">{t('dataPrivacy.rights.contact')}</p>
        </Section>

        {/* Automated decisions */}
        <Section icon={Trash2} titleKey="dataPrivacy.automated.title">
          <p>{t('dataPrivacy.automated.description')}</p>
          <p>{t('dataPrivacy.automated.noDecisions')}</p>
        </Section>

        {/* Cookies & local storage */}
        <Section icon={Cookie} titleKey="dataPrivacy.cookies.title">
          <p>{t('dataPrivacy.cookies.intro')}</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('dataPrivacy.cookies.auth')}</li>
            <li>{t('dataPrivacy.cookies.prefs')}</li>
            <li>{t('dataPrivacy.cookies.consent')}</li>
          </ul>
          <p className="mt-2">{t('dataPrivacy.cookies.noTracking')}</p>
        </Section>
      </div>

      <p className="mt-8 text-xs text-muted-foreground">
        {t('dataPrivacy.legalBasis')}
      </p>
    </div>
  );
}
